import { createElement, type ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import { formatCents } from "../../../../../utils/money";
import type { BillCloseTip } from "./useBillCloseTip";

/** Режимът с бакшиш в „Закрий": бакшишът е включен в настройките (или
 *  повторният опит носи записан бакшиш). Иначе модалът е старият. */
export const isTipLayout = (closeTip: Pick<BillCloseTip, "sectionMode" | "recorded">) =>
  closeTip.sectionMode !== "none" || closeTip.recorded !== null;

/**
 * Колко се плаща С МЕТОДА НА СМЕТКАТА (дизайнът „Закриване — всички
 * комбинации"): бакшиш със същия метод влиза в „Общо за плащане" / „Общо"
 * (и в „Получено"/„Ресто" при „В брой"); бакшиш с друг метод се плаща
 * отделно и само се казва под сумата. Всичко е в центове.
 */
export interface TipDue {
  /** Дължимото с метода на сметката — поръчаното (+ бакшиша, ако е със същия метод). */
  dueCents: number;
  hasTip: boolean;
  /** Бакшишът е със същия метод като сметката. */
  tipSame: boolean;
}

export function tipDueOf({
  orderedCents,
  tipCents,
  tipMethodId,
  billMethodId,
}: {
  orderedCents: number;
  tipCents: number;
  tipMethodId: number | null;
  billMethodId: number | null;
}): TipDue {
  const hasTip = tipCents > 0;
  const tipSame = hasTip && tipMethodId !== null && tipMethodId === billMethodId;
  return { dueCents: orderedCents + (tipSame ? tipCents : 0), hasTip, tipSame };
}

type CloseTipSource = Pick<BillCloseTip, "sectionMode" | "tipEntry" | "recorded">;

/** Дължимото с метода на сметката от бакшиша, който се въвежда, или
 *  (повторен опит) записания — и сумата в бутона „Закриване · X EUR". */
export function closeTipDueOf({
  closeTip,
  orderedCents,
  billMethodId,
}: {
  closeTip: CloseTipSource;
  orderedCents: number;
  billMethodId: number | null;
}): TipDue & { tipCents: number; tipMethodId: number | null } {
  const entry = closeTip.sectionMode === "entry" ? closeTip.tipEntry : null;
  const recorded = closeTip.recorded;
  const tipCents = entry ? entry.tipCents : (recorded?.cents ?? 0);
  const tipMethodId = entry ? entry.tipMethodId : (recorded?.methodId ?? null);
  return { ...tipDueOf({ orderedCents, tipCents, tipMethodId, billMethodId }), tipCents, tipMethodId };
}

/**
 * „Общо"/„Общо за плащане" и редът под него — общо за „Закрий" и „Раздели и
 * плати": бакшишът, който се въвежда, или (повторен опит) записаният; със
 * същия метод — „Сметка X + бакшиш Y", с друг — „Бакшиш Y EUR отделно · метод".
 */
export function useTipDueSummary({
  closeTip,
  orderedCents,
  billMethodId,
  methodNameOf,
}: {
  closeTip: CloseTipSource;
  orderedCents: number;
  billMethodId: number | null;
  methodNameOf: (methodId: number) => string;
}): TipDue & { tipCents: number; tipMethodId: number | null; note: ReactNode } {
  const { t } = useTranslation();
  const due = closeTipDueOf({ closeTip, orderedCents, billMethodId });
  const { tipCents, tipMethodId } = due;
  // Сумите — с моно шрифта на числата (дизайнът), бакшишът — в оранжевото
  // на модула; думите — с текста около тях.
  const amount = createElement("span", {
    className: "font-pos-mono font-semibold text-pos-ink2 [font-variant-numeric:tabular-nums]",
  });
  const tipAmount = createElement("span", {
    className: "font-pos-mono font-bold text-pos-accent [font-variant-numeric:tabular-nums]",
  });
  const note = !due.hasTip
    ? undefined
    : due.tipSame
      ? createElement(Trans, {
          i18nKey: "closeTable.tip.dueWithTip",
          values: { ordered: formatCents(orderedCents), tip: formatCents(tipCents) },
          components: { num: amount, tip: tipAmount },
        })
      : createElement(Trans, {
          i18nKey: "closeTable.tip.dueSeparateTip",
          values: {
            tip: formatCents(tipCents),
            method:
              tipMethodId !== null ? methodNameOf(tipMethodId) : t("closeTable.tip.chooseMethod"),
          },
          components: { tip: tipAmount },
        });
  return { ...due, note };
}
